/**
 * Store Module - Product React Query Hooks
 *
 * useProducts  — fetches the product list (with optional search)
 * useProduct   — fetches a single product by ID
 *
 * Both hooks are backed by @tanstack/react-query and reuse the shared
 * QueryClient configured in main.tsx.
 */

import { useQuery } from '@tanstack/react-query'
import { listProducts, getProduct } from '../services/product-api'
import type { Product } from '../types/product'
import type { UseQueryResult } from '@tanstack/react-query'

/**
 * Fetch all products, optionally filtered by a search string.
 * Query key: ['products', search] — refetches automatically when search changes.
 */
export function useProducts(search?: string): UseQueryResult<Product[], Error> {
  return useQuery<Product[], Error>({
    queryKey: ['products', search ?? ''],
    queryFn: () => listProducts(search),
  })
}

/**
 * Fetch a single product by ID.
 * Query is disabled when id is falsy to avoid spurious network requests.
 */
export function useProduct(id: string): UseQueryResult<Product, Error> {
  return useQuery<Product, Error>({
    queryKey: ['product', id],
    queryFn: () => getProduct(id),
    enabled: Boolean(id),
  })
}
